import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
// import throttle from "throttle-debounce/throttle";
import {
  useReactTable,
  getCoreRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender
} from "@tanstack/react-table";
import wNumb from "wnumb";

const numberFormat = wNumb({
  mark: ".",
  thousand: ","
}).to;

class ParameterOption extends React.PureComponent {
  render () {
    const e = this.props.opt;
    // console.log(e)

    const input = <input type="checkbox"
                         value={e.value}
                         checked={this.props.checked}
                         onChange={this.props.onChange(e)}
                  />;

    return (
      <div className={`option ${e.usageLevel}`}>
        <div><label>{input} {e.label}</label></div>
        <div style={{ marginLeft: "10px" }}>Has {e.standardsCount} standards defined in {e.countriesCount} countries</div>
      </div>);
  }
}

class RecursiveParameter extends React.Component {
  render () {
    let result;
    const textFilter = this.props.textFilter;
    const optionHidden = e => {
      const excludedByUsageLevel = !this.props.showSpecialized && e.usageLevel === "specialized";
      const excludedByTextFilter = textFilter && e.lowerCaseLabel.search(textFilter) === -1;

      return excludedByTextFilter || excludedByUsageLevel;
    };

    if (this.props.levelIndex === 1) {
      const options = this.props.values.map(e => {
        const checked = !!this.props.selected[e.value];
        const hidden = optionHidden(e);
        const classes = {
          hidden: hidden
        };

        const key = `${e.value}-${hidden}-${checked}`;
        // console.log(key)
        const opt = <ParameterOption opt={e} checked={checked} onChange={this.props.onSelect} />;

        return (
          <div key={key} className={classNames(classes)}>
            {opt}
          </div>
        );
      });

      result = <div className="parameter-level">{options}</div>;
    } else {
      result = this.props.values.map(([subGroup, options]) => {
        return <RecursiveParameter key={subGroup}
                                   levelIndex={1}
                                   group={subGroup}
                                   textFilter={this.props.textFilter}
                                   showSpecialized={this.props.showSpecialized}
                                   values={options}
                                   selected={this.props.selected}
                                   onSelect={this.props.onSelect} />;
      });
    }

    return (<div className="parameter-level">
      <h5>{this.props.group}</h5>
      {result}
    </div>);
  }
}

// v6 `filterMethod` for the grouping-name columns: a substring match on the
// leaf row value (synthetic/grouped rows always pass). Re-expressed as a v8
// filterFn (kept inert — the grouping columns are filterable:false, mirroring v6).
const downcaseStringFilter = (textFilter, label) => {
  if (!label) { return true; }
  if (!textFilter) { return true; }

  return label.toLowerCase().search(textFilter.toLowerCase()) !== -1;
};

const simpleFilter = (row, columnId, filterValue) => {
  if (row.getIsGrouped()) { return true; }

  return downcaseStringFilter(filterValue, row.getValue(columnId));
};

// TanStack v8's row.getLeafRows() is `flattenBy(row.subRows, d => d.subRows)` — it returns ALL
// descendant rows INCLUDING the intermediate grouping (subGroup pivot) rows, not just the terminal
// parameter rows. With the two-level grouping (["group","subGroup"]) a top-level CATEGORY row's
// getLeafRows() therefore returns its subGroup pivot rows on top of the real parameters, inflating
// every category aggregate by the number of subgroups — the regression this fixes: the picker showed
// e.g. "Measurement 11 of 50" / "Chemical 0 of 426" where prod showed "9 of 43" / "0 of 420" (and
// the leaked pivot rows' first-child `original` also inflated the # of standards + the selected
// count). react-table v6 (prod) aggregated leaf VALUES only (data rows), so prod is correct.
// Keep only the real parameter (non-grouped) rows.
const dataLeafRows = row => row.getLeafRows().filter(r => !r.getIsGrouped());

// Custom select-dropdown filter rendered in the v8 header cell. Mirrors the v6
// per-column `Filter` UI; reads/writes the controlled columnFilters state through
// column.getFilterValue()/setFilterValue.
function SelectFilter({ column, options, onMountSetter }) {
  const value = column.getFilterValue();
  // Expose the setter so the panel can imperatively reset it (the v6 code held
  // `this.changeSelectedParameterFilter` / `this.changeParameterNameFilter`).
  React.useEffect(() => {
    if (onMountSetter) { onMountSetter(v => column.setFilterValue(v)); }
  }, [column, onMountSetter]);

  return (
    <select
      onChange={event => column.setFilterValue(event.target.value)}
      style={{ width: "100%" }}
      value={value || "all"}
    >
      {options.map(([optValue, optLabel]) => (
        <option key={optValue} value={optValue}>{optLabel}</option>
      ))}
    </select>
  );
}

SelectFilter.propTypes = {
  column: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  onMountSetter: PropTypes.func
};

// Memoized leaf (parameter) row. A checkbox tick changes the selection, which re-renders
// the whole table; without memoization React reconciles EVERY rendered leaf row, so a tick
// inside the largest subgroup (~1,000 parameters) blocked the main thread for hundreds of ms
// (a staging-QA regression). React.memo skips a leaf whose props are referentially equal, so
// only the toggled row (whose `checked` flipped) actually re-renders — the other ~999 are
// skipped. `checked` is passed purely as the memo trigger; the cell renderers read the LIVE
// selection through flexRender (the column defs read `selectionRef.current`). `row`/`headers`
// stay referentially stable across ticks because the column defs no longer churn (see the
// `columns` memo below), so the TanStack row model is not rebuilt. Group/subgroup rows are
// deliberately NOT memoized — their "X of Y selected" aggregate must recompute every tick, and
// there are only ~tens of them.
const LeafRow = React.memo(function LeafRow({ row, headers }) {
  return (
    <div className="rt-tr-group">
      <div className={classNames("rt-tr", row.index % 2 ? "-even" : "-odd")}>
        <div className="rt-td" style={{ paddingLeft: `${row.depth * 15}px` }}>
          {row.getValue("label")}
        </div>
        {headers.map(header => {
          const meta = header.column.columnDef.meta || {};
          if (header.column.id === "label") {
            return <div key={header.id} className="rt-td" style={meta.minWidth ? { minWidth: meta.minWidth } : undefined} />;
          }
          const cell = row.getVisibleCells().find(c => c.column.id === header.column.id);
          return (
            <div key={header.id} className="rt-td" style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}>
              {cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
});

LeafRow.propTypes = {
  row: PropTypes.object.isRequired,
  headers: PropTypes.array.isRequired,
  // `checked` is unused in the body — it is the React.memo trigger: when this leaf's
  // selection flips, `checked` changes and the row re-renders (re-reading live selection).
  checked: PropTypes.bool.isRequired
};

// Headless (TanStack v8) replacement for the v6 pivot <ReactTable>. Emits the
// same `.ReactTable` / `.rt-*` DOM the SCSS (.parameter-detail .ReactTable, the
// .parameter-panel overlay) targets. Grouping = ["group","subGroup"], custom
// aggregated/leaf cells, per-column select filters, controlled expanded/filters/
// sorting state owned by the ParameterPivotTable class above.
function ParameterPivotTableInner(props) {
  const {
    options,
    selectedOptionIdObject,
    onSelect,
    sorting,
    onSortingChange,
    columnFilters,
    onColumnFiltersChange,
    expanded,
    onExpandedChange,
    registerSelectedFilterSetter,
    registerStandardsFilterSetter
  } = props;

  // The selection changes on every checkbox tick. Reading it through a ref (kept current
  // each render) lets the column defs + cell renderers see the latest selection WITHOUT
  // listing `selectedOptionIdObject` as a `columns` memo dep — which would force TanStack to
  // rebuild every row model (core→grouped→sorted→filtered→expanded) on every tick over all
  // ~2,000 parameter rows (a chunk of that regression's cost). Cells re-read `selectionRef.current` when
  // their row re-renders.
  const selectionRef = React.useRef(selectedOptionIdObject);
  selectionRef.current = selectedOptionIdObject;

  const makeInput = row => {
    // v8 grouped (synthetic pivot) rows: render the Select all / Clear all
    // buttons over every leaf parameter beneath this group.
    if (row.getIsGrouped()) {
      const leafOptions = dataLeafRows(row).map(r => r.original);

      const addObject = { addOptions: leafOptions };
      const clearObject = { removeOptions: leafOptions };

      return (
        <span>
          <a className="btn btn-primary" onClick={onSelect(addObject)}>Select all</a>
          <a className="btn btn-default" onClick={onSelect(clearObject)}>Clear all</a>
        </span>
      );
    }

    const parameterObject = row.original;
    const value = parameterObject.value;
    const checked = !!selectionRef.current[value];

    return <input type="checkbox"
                  style={{height: "30px", width: "30px"}}
                  value={value}
                  checked={checked}
                  onChange={onSelect(parameterObject)}
    />;
  };

  // Of the column defs, only the "Selected" column FILTER depends on the live selection
  // (Selected / Not selected narrows rows by what's ticked). When that filter is active a tick
  // must re-run filtering, so let `columns` (and therefore the row models) rebuild on selection
  // change in that case only; otherwise keep `columns` referentially stable so a tick is O(1)
  // and the memoized leaf rows above can skip. The "# of Standards" filter reads usageLevel
  // (not selection) and the checkbox `checked` is read via selectionRef, so neither needs this.
  const selectionFilterActive = columnFilters.some(
    f => f.id === "selectedState" && (f.value === "selected" || f.value === "not-selected")
  );

  const columns = React.useMemo(() => [
    {
      header: "Category",
      accessorKey: "group",
      enableColumnFilter: false,
      filterFn: simpleFilter,
      meta: { minWidth: 20 }
    },
    {
      header: "Subcategory",
      accessorKey: "subGroup",
      enableColumnFilter: false,
      filterFn: simpleFilter,
      meta: { minWidth: 40 }
    },
    {
      header: "Parameter",
      accessorKey: "label",
      enableColumnFilter: false,
      // Aggregated count is computed live from the leaf rows in the cell render
      // so it always reflects the current selection (no row-model memo staleness).
      aggregatedCell: ({ row }) => {
        const leaves = dataLeafRows(row);
        const total = leaves.length;
        const selected = leaves.filter(r => selectionRef.current[r.original.value]).length;
        return <span>{numberFormat(selected)} of {numberFormat(total)} selected</span>;
      },
      meta: { minWidth: 20 }
    },
    {
      header: "Selected",
      accessorKey: "selectedState",
      enableColumnFilter: true,
      aggregatedCell: ({ row }) => makeInput(row),
      cell: ({ row }) => makeInput(row),
      filterFn: (row, columnId, filterValue) => {
        if (row.getIsGrouped()) { return true; }
        const parameterObject = row.original;
        if (!parameterObject) { return true; }

        if (filterValue === "all") { return true; }
        if (filterValue === "selected") {
          return !!selectionRef.current[parameterObject.value];
        }
        if (filterValue === "not-selected") {
          return !selectionRef.current[parameterObject.value];
        }
        return true;
      },
      meta: {
        minWidth: 20,
        filterUi: {
          register: registerSelectedFilterSetter,
          options: [
            ["all", "Show All"],
            ["selected", "Selected"],
            ["not-selected", "Not selected"]
          ]
        }
      }
    },
    {
      header: "# of Standards",
      accessorKey: "standardsCount",
      enableColumnFilter: true,
      aggregatedCell: ({ row }) => {
        const total = dataLeafRows(row).reduce((a, r) => a + (r.original.standardsCount || 0), 0);
        return <span>{numberFormat(total)} standards</span>;
      },
      cell: ({ getValue }) => <span>{numberFormat(getValue())} standards</span>,
      filterFn: (row, columnId, filterValue) => {
        if (row.getIsGrouped()) { return true; }
        const parameterObject = row.original;
        if (!parameterObject) { return true; }

        if (filterValue === "all") { return true; }
        return parameterObject.usageLevel === filterValue;
      },
      meta: {
        minWidth: 20,
        filterUi: {
          register: registerStandardsFilterSetter,
          options: [
            ["all", "Show All"],
            ["common", "Common (>= 5 standards)"],
            ["specialized", "Specialized(<5 standards)"]
          ]
        }
      }
    }
  ], [onSelect, selectionFilterActive ? selectedOptionIdObject : null]);

  const table = useReactTable({
    // v6's <ReactTable data={undefined}> rendered empty rather than crashing;
    // redux can briefly pass undefined options before the parameter list loads.
    data: options || [],
    columns,
    // grouping is fixed (pivotBy in v6) → uncontrolled via initialState so v8
    // doesn't demand an onGroupingChange handler / log a controlled-state warning.
    initialState: {
      grouping: ["group", "subGroup"]
    },
    state: {
      sorting,
      columnFilters,
      expanded
    },
    onSortingChange,
    onColumnFiltersChange,
    onExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetExpanded: false,
    enableColumnResizing: false
  });

  // Visible (non-grouping) columns carry the header + filter UI; the two grouping
  // columns are pivoted away (their values fold into the expander row), matching v6.
  // Memoized so `headers` stays referentially stable across ticks — it is a prop of the
  // memoized LeafRow, so a new array each render would defeat React.memo. Recomputes only
  // when the table or the column set changes (the latter on a selection-filter toggle).
  const headers = React.useMemo(
    () => table.getFlatHeaders().filter(h => !["group", "subGroup"].includes(h.column.id)),
    [table, columns]
  );

  const headerCells = headers.map(header => {
    const canSort = header.column.getCanSort();
    const sortDir = header.column.getIsSorted();
    const meta = header.column.columnDef.meta || {};
    return (
      <div
        key={header.id}
        className={classNames(
          "rt-th",
          canSort && "-cursor-pointer",
          sortDir === "desc" && "-sort-desc",
          sortDir === "asc" && "-sort-asc"
        )}
        style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}
        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
      >
        <div className="rt-resizable-header-content">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </div>
    );
  });

  const filterCells = headers.map(header => {
    const meta = header.column.columnDef.meta || {};
    return (
      <div key={header.id} className="rt-th" style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}>
        {header.column.getCanFilter() && meta.filterUi ? (
          <SelectFilter
            column={header.column}
            options={meta.filterUi.options}
            onMountSetter={meta.filterUi.register}
          />
        ) : null}
      </div>
    );
  });

  const renderRow = row => {
    if (row.getIsGrouped()) {
      // Grouped (pivot) row: the grouping value + expander, then the aggregated
      // cells for the visible columns. v6 rendered the group label in the pivoted
      // first cell with a toggle.
      const groupingValue = row.groupingValue;
      return (
        <div className="rt-tr-group" key={row.id}>
          <div className={classNames("rt-tr", row.index % 2 ? "-even" : "-odd")}>
            <div
              className="rt-td rt-expandable -cursor-pointer"
              style={{ paddingLeft: `${row.depth * 15}px` }}
              onClick={row.getToggleExpandedHandler()}
            >
              <span className="rt-expander">{row.getIsExpanded() ? "▼" : "▶"}</span>
              {" "}{groupingValue}
            </div>
            {headers.map(header => {
              const cell = row.getVisibleCells().find(c => c.column.id === header.column.id);
              const meta = header.column.columnDef.meta || {};
              return (
                <div key={header.id} className="rt-td" style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}>
                  {cell ? flexRender(cell.column.columnDef.aggregatedCell || cell.column.columnDef.cell, cell.getContext()) : null}
                </div>
              );
            })}
          </div>
          {row.getIsExpanded()
            ? row.subRows.map(sub => renderRow(sub))
            : null}
        </div>
      );
    }

    // Leaf row — rendered via the memoized LeafRow so a checkbox tick only re-renders the
    // toggled row, not every leaf in an expanded subgroup. `checked` is the memo trigger.
    const checked = !!selectionRef.current[row.original.value];
    return <LeafRow key={row.id} row={row} headers={headers} checked={checked} />;
  };

  // getRowModel().rows is the FLAT, expansion-aware list — it contains the depth-0 group rows AND
  // every currently-expanded descendant (subgroup + leaf) row. renderRow ALREADY recurses into
  // `row.subRows` for expanded rows, so mapping the flat list would render each expanded subgroup
  // (and its leaves) TWICE — the duplicate "Organic compounds" subgroup rows. Map only the
  // depth-0 group rows and let renderRow's recursion emit the descendants exactly once.
  const rows = table.getRowModel().rows.filter(row => row.depth === 0);

  return (
    <div className="ReactTable">
      <div className="rt-table">
        <div className="rt-thead -header">
          <div className="rt-tr">
            <div className="rt-th" />
            {headerCells}
          </div>
        </div>
        <div className="rt-thead -filters">
          <div className="rt-tr">
            <div className="rt-th" />
            {filterCells}
          </div>
        </div>
        <div className="rt-tbody">
          {rows.map(row => renderRow(row))}
        </div>
      </div>
    </div>
  );
}

ParameterPivotTableInner.propTypes = {
  options: PropTypes.array.isRequired,
  selectedOptionIdObject: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  sorting: PropTypes.array.isRequired,
  onSortingChange: PropTypes.func.isRequired,
  columnFilters: PropTypes.array.isRequired,
  onColumnFiltersChange: PropTypes.func.isRequired,
  expanded: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  onExpandedChange: PropTypes.func.isRequired,
  registerSelectedFilterSetter: PropTypes.func,
  registerStandardsFilterSetter: PropTypes.func
};

class ParameterPivotTable extends React.Component {
  // v6 default-expanded only the top-level GROUPS (its `{0:{},1:{},2:{},3:{}}` opened
  // the 4 group rows but left their subgroups collapsed) AND paginated to 6 rows, so the
  // panel opened with ~tens of rows. The TanStack v8 rewrite used `expanded: true`, which
  // opens EVERY group AND subgroup — dumping all ~2,000 parameter leaf rows into the DOM
  // at once. Every checkbox tick then synchronously re-rendered that whole tree (~450ms
  // main-thread block per tick on real prod data — a staging-QA regression). Restore the
  // v6 behaviour: expand only the depth-0 group rows, leaving subgroups collapsed until the
  // user clicks one. TanStack v8 grouped-row ids are `<groupingColumnId>:<value>` at depth 0
  // (getGroupedRowModel: `id = `${columnId}:${groupingValue}``), i.e. `group:<value>`;
  // deriving them from the option list keeps the leaf rows out of the DOM on open.
  defaultExpanded() {
    return (this.props.options || []).reduce((acc, o) => {
      acc[`group:${o.group}`] = true;
      return acc;
    }, {});
  }

  constructor(props) {
    super(props);

    this.state = { expanded: this.defaultExpanded(), sorting: [], columnFilters: [] };

    // Imperative setters for the two select filters (the v6 code held
    // `changeSelectedParameterFilter` / `changeParameterNameFilter`).
    this.registerSelectedFilterSetter = setter => { this.changeSelectedParameterFilter = setter; };
    this.registerStandardsFilterSetter = setter => { this.changeParameterNameFilter = setter; };

    this.onSortingChange = updater => {
      const next = typeof updater === "function" ? updater(this.state.sorting) : updater;
      // v6 reset expansion on sort.
      this.setState({ sorting: next, expanded: this.defaultExpanded() });
    };
    this.onColumnFiltersChange = updater => {
      const next = typeof updater === "function" ? updater(this.state.columnFilters) : updater;
      // v6 reset expansion on filter change.
      this.setState({ columnFilters: next, expanded: this.defaultExpanded() });
    };
    this.onExpandedChange = updater => {
      const next = typeof updater === "function" ? updater(this.state.expanded) : updater;
      this.setState({ expanded: next });
    };
  }

  componentDidUpdate(prevProps) {
    const differentOptions = prevProps.activePurposeId !== this.props.activePurposeId;
    if (differentOptions) {
      this.setState({ expanded: this.defaultExpanded(), columnFilters: [] });
    }
  }

  render () {
    return <ParameterPivotTableInner
      options={this.props.options}
      selectedOptionIdObject={this.props.selectedOptionIdObject}
      onSelect={this.props.onSelect}
      sorting={this.state.sorting}
      onSortingChange={this.onSortingChange}
      columnFilters={this.state.columnFilters}
      onColumnFiltersChange={this.onColumnFiltersChange}
      expanded={this.state.expanded}
      onExpandedChange={this.onExpandedChange}
      registerSelectedFilterSetter={this.registerSelectedFilterSetter}
      registerStandardsFilterSetter={this.registerStandardsFilterSetter}
    />;
  }
}

class ParameterPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = { textFilter: null, textValue: "", showSpecialized: false };
  }
  render () {
    const show = this.props.expandParameterPanel;
    const hide = this.props.collapseParameterPanel;

    const classes = classNames({
      "overlay-with-form-in-middle": true,
      hidden: this.props.hidden
    });

    const parameterCount = this.props.selectedValues.length;

    let component, options, buttonComponent;

    buttonComponent = <div className="btn btn-primary" onClick={show}>{parameterCount} selected.</div>;

    const selectedObject = this.props.selectedValues.reduce((a, e) => { a[e.value] = true; return a; }, {});

    options = <ParameterPivotTable options={this.props.options}
                                   selectedOptionIdObject={selectedObject}
                                   onSelect={this.props.onOptionSelect}
                                   hidden={this.props.hidden}
                                   activePurposeId={this.props.activePurposeId}
              />;
    component = <div className={classes}>
      <div className="form">
        <h4 style={{ padding: "10px", cursor: "auto" }}>
          <div className="row">
            <div className="col-6">
              Filter Parameters
            </div>
            <div className="col-3">
              <a className="btn btn-block btn-default" onClick={this.props.onOptionSelect({removeOptions: this.props.options})}>Clear all</a>
            </div>
            <div className="col-3">
              <a className="btn btn-block btn-primary" onClick={hide}>Done</a>
            </div>
          </div>
        </h4>
        <div style={{ padding: 0 }} className="options recursive-option-wrapper">
          {options}
        </div>
      </div>
    </div>;

    return (
      <div className="parameter-panel form-group col-md-4">
        <h4>{this.props.filterTitle}</h4>
        {buttonComponent}
        {component}
      </div>
    );
  }
}

const valueType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
const optionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: valueType.isRequired
});

const optionShapeList = PropTypes.arrayOf(optionShape);

ParameterOption.propTypes = {
  opt: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

RecursiveParameter.propTypes = {
  group: PropTypes.string,
  values: PropTypes.array.isRequired,
  levelIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.object.isRequired,
  textFilter: PropTypes.string,
  showSpecialized: PropTypes.bool.isRequired
};

ParameterPivotTable.propTypes = {
  hidden: PropTypes.bool.isRequired,
  selectedOptionIdObject: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  activePurposeId: PropTypes.number
};

ParameterPanel.propTypes = {
  filterTitle: PropTypes.string.isRequired,
  hidden: PropTypes.bool.isRequired,
  selectedValues: optionShapeList.isRequired,
  options: PropTypes.array,
  onOptionSelect: PropTypes.func.isRequired,
  expandParameterPanel: PropTypes.func.isRequired,
  collapseParameterPanel: PropTypes.func.isRequired,
  activePurposeId: PropTypes.number
};

export default ParameterPanel;
